import React, { useEffect, useMemo, useState } from "react";
import MyTable from "../ui/Table";
import {
  Dropdown as AntdDropdown,
  Button,
  Menu,
  Input,
  Space,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import apiRoutes from "../api/apiRoutes";
import { apiGet, apiPost, apiPut } from "../api/request";
import AddReaderModal from "../modals/AddReaderModal";
import EditReaderModal from "../modals/EditReaderModal";
import axios from "axios";
import ManageSubscriptionModal from "../modals/ManageSubscriptionModal";
import DeleteReaderModal from "../modals/DeleteConfirmationModal";
import GlowButton from "../components/GlowButton";

const Readers = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isMangeProfileModalVisible, setIsManageProfileModalVisible] =
    useState(false);
  const [editingReaderId, setEditingReaderId] = useState(null);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    mobile: "",
    state_id: 0,
    jobtitle: "",
    password: "",
    confirmpassword: "",
  });

  const [editFormData, setEditFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    mobile: "",
    state_id: 0,
    jobtitle: "",
    status: "",
    subscription_type: "",
    expiry_date: "",
  });

  const [subscriptionFormData, setSubscriptionFormData] = useState({
    id: "",
    name: "",
    is_expired: "",
    start_date: "",
    end_date: "",
    status: "",
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    states: [],
  });

  const [searchText, setSearchText] = useState("");
  const [readers, setReaders] = useState([]);

  const showModal = () => {
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      state_id: 0,
      jobtitle: "",
      password: "",
      confirmpassword: "",
    });
    setIsModalVisible(true);
  };

  const showEditModal = () => setIsEditModalVisible(true);

  const showProfileModal = () => setIsManageProfileModalVisible(true);

  const hideModal = () => {
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      state_id: 0,
      jobtitle: "",
      password: "",
      confirmpassword: "",
    });
    setIsModalVisible(false);
  };

  const hideEditModal = () => {
    setEditFormData({
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      state_id: 0,
      jobtitle: "",
    });
    setEditingReaderId(null);
    setIsEditModalVisible(false);
  };

  const hideProfileModal = () => {
    setSubscriptionFormData({
      id: "",
      start_date: "",
      end_date: "",
      status: "",
    });
    setIsManageProfileModalVisible(false);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Fetch states
        const statesRes = await apiGet(apiRoutes.getStates);
        const states = Array.isArray(statesRes)
          ? statesRes.map((state) => ({
              label: state.name,
              value: state.id,
            }))
          : [];

        setDropdownOptions((prev) => ({ ...prev, states }));

        // Fetch readers
        const readersRes = await apiGet(apiRoutes.getAllReaders);
        if (Array.isArray(readersRes.readers)) {
          setReaders(readersRes.readers);
        }
      } catch (err) {
        console.error("Failed to fetch initial data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    console.log("dropdown options (updated):", dropdownOptions);
  }, [dropdownOptions]);

  const fetchReaders = async () => {
    try {
      setLoading(true);
      const res = await apiGet(apiRoutes.getAllReaders);
      if (Array.isArray(res.readers)) {
        setReaders(res.readers);
      }
    } catch (err) {
      console.error("Failed to fetch Readers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate password match
      if (formData.password !== formData.confirmpassword) {
        alert("Passwords do not match!");
        return;
      }

      setLoading(true);

      const payload = {
        first_name: formData.firstname,
        last_name: formData.lastname,
        email: formData.email,
        mobile: formData.mobile,
        job_title: formData.jobtitle,
        state_id: formData.state_id,
        password: formData.password,
        role: "reader",
      };

      console.log("Reader data:", payload);
      const res = await apiPost(apiRoutes.createReader, payload);

      hideModal();

      const { payment, first_name, last_name, email, mobile } = res.data;
      console.log("Reader destructured:", res, payment);

      if (!payment || !payment.order_id) {
        throw new Error("Payment details not available");
      }

      // Load Razorpay Checkout script
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () =>
        openRazorpayCheckout({
          payment,
          first_name,
          last_name,
          email,
          mobile,
        });
      script.onerror = () => console.log("Failed to load Razorpay SDK");
      document.body.appendChild(script);
    } catch (err) {
      console.error("Failed to create reader:", err);
      alert(
        "Failed to create reader: " +
          (err.response?.data?.error || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        id: editingReaderId.toString(),
        first_name: editFormData.firstname,
        last_name: editFormData.lastname,
        email: editFormData.email,
        mobile: editFormData.mobile,
        job_title: editFormData.jobtitle,
        state_id: editFormData.state_id,
        status: editFormData.status,
        subscription_type: editFormData.subscription_type,
        expiry_date: editFormData.expiry_date,
      };

      console.log("Updating reader data:", payload);
      await apiPost(apiRoutes.updateUser, payload);

      alert("Reader updated successfully!");
      hideEditModal();
      fetchReaders();
    } catch (err) {
      console.error("Failed to update reader:", err);
      alert(
        "Failed to update reader: " +
          (err.response?.data?.error || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      console.log("Deleting reader data:", editingReaderId);
      await apiGet(apiRoutes.deleteUser(editingReaderId));

      alert("Reader deleted successfully!");
      setDeleteModalVisible(false);
      fetchReaders();
    } catch (err) {
      console.error("Failed to delete reader:", err);
      setDeleteModalVisible(false);
      alert(
        "Failed to delete reader: " +
          (err.response?.data?.error || err.message),
      );
    } finally {
      setDeleteModalVisible(false);
      setLoading(false);
    }
  };

  const openRazorpayCheckout = ({
    payment,
    first_name,
    last_name,
    email,
    mobile,
  }) => {
    if (!payment.order_id || !payment.amount || !payment.currency) {
      console.error("Invalid payment data:", payment);
      alert("Cannot process payment: incomplete data");
      return;
    }

    console.log("Razorpay key:", process.env.REACT_APP_RAZORPAY_KEY_ID);
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: payment.amount,
      currency: payment.currency,
      order_id: payment.order_id,
      name: "Maotor Vehicles Law - Ebook",
      description: "Book Subscription Payment",
      image:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAgEAAAIBCAYAAADQ5mxh...",
      prefill: {
        name: `${first_name} ${last_name}`,
        email: email,
        contact: mobile,
      },
      theme: {
        color: "#000000",
      },
      handler: async function (response) {
        console.log("Payment response:", response);
        if (
          !response.razorpay_payment_id ||
          !response.razorpay_order_id ||
          !response.razorpay_signature
        ) {
          console.error("Invalid payment response:", response);
          alert("Payment verification failed: incomplete data");
          return;
        }
        try {
          setLoading(true);
          const confirmRes = await axios.post(
            "https://mvdapi-mxjdw.ondigitalocean.app/api/user/reader/confirm",
            {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
            },
          );
          console.log("Confirmation response:", confirmRes);
          alert(
            "Payment successful! Payment ID: " + response.razorpay_payment_id,
          );
          await fetchReaders();
          window.location.href = "/payment-success";
        } catch (err) {
          console.error(
            "Failed to confirm payment:",
            err.response?.data?.error || err.message,
          );
          alert(
            "Payment confirmation failed: " +
              (err.response?.data?.error || err.message),
          );
        } finally {
          setLoading(false);
        }
      },
      modal: {
        ondismiss: function () {
          console.log("Payment modal closed");
          alert("Payment process was cancelled");
          fetchReaders();
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", function (response) {
      console.error("Payment failed:", response.error);
      alert("Payment failed: " + response.error.description);
    });
    rzp.open();
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const filteredData = useMemo(() => {
    return readers.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchText.toLowerCase()),
      ),
    );
  }, [searchText, readers]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (text?.trim() ? text : "Unknown"),
    },
    {
      title: "Phone",
      dataIndex: "mobile",
      key: "mobile",
      render: (text) => (text ? text : "-"),
    },
    {
      title: "State",
      dataIndex: "state_name",
      key: "state_name",
      render: (text) => (text ? text : "-"),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      key: "job_title",
      render: (text) => (text ? text : "-"),
    },
    {
      title: "Joined On",
      dataIndex: "date_of_join",
      key: "date_of_join",
      render: (text) => (text ? text : "-"),
    },
    {
      title: "Subscription",
      dataIndex: "subscription",
      key: "subscription",
      align: "center",
      render: (_, record) => {
        const status = record?.subscription?.status || "inactive";

        const capitalizeFirstLetter = (string) => {
          if (!string) return "";
          return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        };

        const displayStatus = capitalizeFirstLetter(status);

        return <span>{displayStatus}</span>;
      },
      filters: [
        { text: "Trial", value: "trial" },
        { text: "Pending", value: "pending" },
        { text: "Annual", value: "Annual" },
      ],
      onFilter: (value, record) => record.subscription.status === value,
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry",
      key: "expiry",
      render: (_, record) =>
        record.subscription ? record.subscription.end_date : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (_, record) => {
        const status = record ? record.status : "Expired";
        const lowerCaseStatus = status.toLowerCase();

        const capitalizeFirstLetter = (string) => {
          if (!string) return "";
          return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        };

        const displayStatus = capitalizeFirstLetter(status);
        const textColor = lowerCaseStatus === "active" ? "green" : "red";

        return <span style={{ color: textColor }}>{displayStatus}</span>;
      },
      filters: [
        { text: "Active", value: "Active" },
        { text: "Expired", value: "Expired" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <AntdDropdown
          overlay={
            <Menu
              onClick={({ key }) => handleMenuClick(record, key)}
              items={[
                { label: "Manage", key: "manage", icon: <SettingOutlined /> },
                { label: "Delete", key: "delete", icon: <DeleteOutlined /> },
              ]}
            />
          }
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </AntdDropdown>
      ),
    },
  ];

  const handleMenuClick = (record, action) => {
    console.log(`Action: ${action} on`, record);
    if (action === "manage") {
      // Populate edit form with reader data

      const [firstName, ...lastNameParts] = record.name.split(" ");
      const lastName = lastNameParts.join(" ");

      setEditFormData({
        firstname: firstName || "",
        lastname: lastName || "",
        email: record.email || "",
        mobile: record.mobile || "",
        state_id: record.state_id || 0,
        status: record.status || "",
        jobtitle: record.job_title || "",
        subscription_type: record.subscription
          ? record.subscription.status
          : "",
        expiry_date: record.subscription ? record.subscription.end_date : "",
      });
      setEditingReaderId(record.id);
      showEditModal();
    } else if (action === "delete") {
      setEditingReaderId(record.id);
      setDeleteModalVisible(true);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-4">Readers List</h1>

      <div className="border border-gray-300 rounded-md">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 p-4">
          <Space className="mb-2 md:mb-0">
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              className="w-[250px]"
            />
          </Space>

          <GlowButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={showModal}
            style={{ backgroundColor: "black", borderColor: "black" }}
          >
            + Create Reader
          </GlowButton>
        </div>
        <div className="border border-gray-200 rounded-md">
          <Spin spinning={loading} tip="Loading..." size="large">
            <MyTable columns={columns} dataSource={filteredData} />
          </Spin>
        </div>
      </div>

      <AddReaderModal
        visible={isModalVisible}
        onCancel={hideModal}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        dropdownOptions={dropdownOptions}
      />

      <EditReaderModal
        visible={isEditModalVisible}
        onCancel={hideEditModal}
        onSubmit={handleUpdateSubmit}
        formData={editFormData}
        setFormData={setEditFormData}
        dropdownOptions={dropdownOptions}
      />

      <ManageSubscriptionModal
        visible={isMangeProfileModalVisible}
        onCancel={hideProfileModal}
        onSubmit={handleSubmit}
        formData={subscriptionFormData}
        setFormData={setSubscriptionFormData}
      />
      <DeleteReaderModal
        visible={isDeleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Readers;
